/**
 * UserManagement component - handles staff user management and sales statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatIST } from '../../lib/store';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { UserPlus, Users, Trash2, ChevronRight, Lock, Settings, Monitor, ShieldOff, Loader2 } from 'lucide-react';

import { useAuth } from '../../lib/store';
import { logger } from '../../lib/logger';

export function UserManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Managers see the Staff tab but can ONLY create operational roles (staff /
  // creator / dispatcher / delivery / stock_manager). Admin/manager creation
  // stays admin-only — enforced on backend too via /auth/register.
  const { user: currentUser } = useAuth();
  // Only an admin manages admin and manager accounts.
  const isManagerOnly = currentUser?.role !== 'admin';
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', staff_code: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [salesStats, setSalesStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Edit user state
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', staff_code: '', phone: '', role: '', email: '', new_email: '', password: '', is_active: true });
  
  // Client-side staff-row filter (name / email / staff_code). Empty = show all.
  const [staffSearch, setStaffSearch] = useState('');
  
  // Passcode protection state. The passcode itself is checked on the
  // SERVER (X-Action-Passcode header) — the browser never compares it.
  // We stash the code in memory for the duration of a single action so
  // the user only enters it once even though the flow spans two dialogs
  // (passcode → form → server-side action).
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');
  const [passcodeAction, setPasscodeAction] = useState(null); // 'add' | 'delete' | 'edit'
  const [pendingPasscode, setPendingPasscode] = useState('');
  
  // Passcode settings (Admin only)
  const [showPasscodeSettings, setShowPasscodeSettings] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState('');
  // "Forgot the passcode?" lane — admin re-enters their own login password
  // instead of the current passcode. Server verifies against the caller's
  // own password_hash via the verify_password helper.
  const [forgotPasscode, setForgotPasscode] = useState(false);
  const [adminLoginPassword, setAdminLoginPassword] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  
  // Request passcode before action
  const requestPasscode = (action) => {
    setPasscodeAction(action);
    setPasscodeInput('');
    setShowPasscodeDialog(true);
  };
  
  // Stash the entered code and open the next step for the action. The
  // server does the real verification — a wrong code surfaces as a 403
  // toast once the user clicks the final Create / Delete / Save button.
  const verifyPasscode = () => {
    if (!passcodeInput) return;
    setPendingPasscode(passcodeInput);
    setShowPasscodeDialog(false);
    setPasscodeInput('');
    if (passcodeAction === 'add') {
      setShowDialog(true);
    } else if (passcodeAction === 'delete' && userToDelete) {
      handleDeleteUser(passcodeInput);
    } else if (passcodeAction === 'edit') {
      handleSaveEdit(passcodeInput);
    }
  };
  
  // Change the server-side action passcode. Local checks are just UX
  // (length + match); the server also enforces >=4 and, when a passcode
  // is already stored, re-verifies the current one. Current-passcode
  // field is OPTIONAL because on first-time setup nothing is stored.
  const handleChangePasscode = async () => {
    if (newPasscode.length < 4) {
      toast.error('New passcode must be at least 4 characters');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('New passcodes do not match');
      return;
    }
    try {
      await api.changeActionPasscode(
        forgotPasscode ? '' : currentPasscode,
        newPasscode,
        forgotPasscode ? adminLoginPassword : '',
      );
      toast.success('Passcode updated successfully');
      setShowPasscodeSettings(false);
      setCurrentPasscode('');
      setAdminLoginPassword('');
      setForgotPasscode(false);
      setNewPasscode('');
      setConfirmPasscode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update passcode');
    }
  };

  const fetchSalesStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await api.getSalesStats({});
      setSalesStats(res.data);
    } catch (err) {
      logger.error('Failed to fetch sales stats:', err);
    }
    setLoadingStats(false);
  }, []);

  useEffect(() => {
    fetchSalesStats();
  }, [fetchSalesStats]);

  // Auto-expand a specific staff card when arriving here via
  // "Back" from the Quotation Detail page. The QuotationDetail Back
  // button navigates to /admin?tab=staff&expandUser=<userId> so the
  // staff-member's card re-opens exactly where they left off. The
  // param is consumed once, then stripped from the URL so a subsequent
  // manual collapse doesn't get re-expanded on re-render.
  useEffect(() => {
    const expandUserId = searchParams.get('expandUser');
    if (expandUserId) {
      setExpandedUser(expandUserId);
      // Drop the param so the URL isn't "sticky" — next time the tab
      // opens without it, users start clean.
      const next = new URLSearchParams(searchParams);
      next.delete('expandUser');
      setSearchParams(next, { replace: true });
      // Scroll the card into view once the list has rendered.
      setTimeout(() => {
        const el = document.querySelector(`[data-user-card-id="${expandUserId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [searchParams, setSearchParams]);

  const handleDeleteUser = async (passcode = pendingPasscode) => {
    if (!userToDelete) return;
    try {
      await api.deleteUser(userToDelete.user_id, passcode);
      toast.success(`${userToDelete.user_name} has been deleted`);
      setUserToDelete(null);
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };
  
  // Initiate delete with passcode
  const initiateDelete = (user) => {
    setUserToDelete(user);
    requestPasscode('delete');
  };

  const [credentialsToShare, setCredentialsToShare] = useState(null);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) { 
      toast.error('All fields are required'); 
      return; 
    }
    setSaving(true);
    try {
      await api.register(form, pendingPasscode);
      toast.success('User created successfully');
      // Capture for credential-share dialog before resetting
      setCredentialsToShare({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
      });
      setShowDialog(false);
      setForm({ name: '', email: '', password: '', role: 'staff', staff_code: '', phone: '' });
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to create user'); 
    } finally { 
      setSaving(false); 
    }
  };

  const buildCredentialMessage = (creds) => {
    if (!creds) return '';
    const url = (typeof window !== 'undefined') ? window.location.origin : 'https://hedaplyquotations.com';
    return (
      `Hi ${creds.name},\n\n` +
      `Your login for HEDA PLYWOODS Quotation App:\n\n` +
      `🔗 Link: ${url}\n` +
      `📧 Email: ${creds.email}\n` +
      `🔐 Password: ${creds.password}\n` +
      `👤 Role: ${creds.role}\n\n` +
      `Please keep these details safe. You can change your password after first login.\n\n` +
      `— HEDA PLYWOODS`
    );
  };

  const handleCopyCredentials = async () => {
    const text = buildCredentialMessage(credentialsToShare);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Credentials copied! Paste in WhatsApp/Email');
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Credentials copied!');
    }
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(buildCredentialMessage(credentialsToShare));
    const phone = credentialsToShare?.phone?.replace(/\D/g, '') || '';
    const url = phone ? `https://wa.me/${phone.startsWith('91') ? phone : '91' + phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };
  
  // Handle editing existing user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      staff_code: user.staff_code || '',
      phone: user.phone || '',
      role: user.role || 'staff',
      email: user.email || '',
      // Fresh blank on every open — treated exactly like the New
      // Password field ('leave blank to keep').
      new_email: '',
      password: '',
      // Missing = active (older accounts never had the field).
      is_active: user.is_active !== false,
    });
  };
  
  const handleSaveEdit = async (passcode = pendingPasscode) => {
    if (!editingUser) return;
    // Only ship fields that actually have a value — sending password=""
    // would attempt to set an empty password (rejected by backend).
    const payload = {
      name: editForm.name,
      staff_code: editForm.staff_code,
      phone: editForm.phone,
    };
    // Only ship role when it actually changed — otherwise the backend
    // would treat a no-op role field as a role change and demand the
    // action passcode on plain name/phone/email edits.
    if (editForm.role !== editingUser.role) payload.role = editForm.role;
    const nextEmail = (editForm.new_email || '').trim();
    if (nextEmail && nextEmail !== editingUser.email) payload.email = nextEmail;
    if (editForm.password && editForm.password.length >= 4) payload.password = editForm.password;
    if (editForm.is_active !== (editingUser.is_active !== false)) payload.is_active = editForm.is_active;

    // Role change OR non-empty new password require the
    // server-side action passcode. Name / staff code / phone / email
    // only edits skip the prompt. If we don't already have a code in
    // hand (from a previous passcode dialog in this action), open the
    // passcode dialog first — verifyPasscode will call us back with
    // the code once collected.
    const needsPasscode =
      editForm.role !== editingUser.role ||
      (editForm.password && editForm.password.length >= 4);
    if (needsPasscode && !passcode) {
      requestPasscode('edit');
      return;
    }

    try {
      await api.updateUser(editingUser.id, payload, passcode);
      toast.success('User updated successfully');
      setEditingUser(null);
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const formatDate = (isoString) => formatIST(isoString);

  return (
    <div className="space-y-6">
      {/* Add User Button + Settings */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <Button 
            onClick={() => requestPasscode('add')} 
            className="h-10 rounded-xl bg-heda-olive px-4 font-semibold text-white shadow-none hover:bg-heda-olive-dark" 
            data-testid="admin-add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" /> Add Staff User
          </Button>
          
          {/* Passcode Settings Button (changing the passcode is admin-only) */}
          {!isManagerOnly && (
          <Button 
            onClick={() => setShowPasscodeSettings(true)} 
            variant="outline"
            className="h-10 rounded-full border-heda-line bg-white text-[12.5px] text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" 
            data-testid="passcode-settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" /> Passcode
          </Button>
          )}
        </div>
      </div>

      {/* Sales by User */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h3 className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink"><Users className="w-4 h-4 text-heda-gold" /> Quotations by Salesperson</h3>
          {/* Staff row filter — client-side match against name / email /
              staff_code. Empty string shows every staff member. */}
          <Input
            value={staffSearch}
            onChange={(e) => setStaffSearch(e.target.value)}
            placeholder="Search staff by name, email or code"
            className="rounded-xl h-10 text-base md:text-sm w-full sm:w-72 bg-white border-heda-line"
            data-testid="staff-search-input"
          />
        </div>
        {loadingStats ? (
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-heda-mute"><Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading...</p>
        ) : (() => {
          const rows = salesStats?.by_user || [];
          const needle = staffSearch.trim().toLowerCase();
          const filtered = needle
            ? rows.filter(u => {
                const code = (u.user_info?.staff_code || '').toLowerCase();
                const name = (u.user_name || '').toLowerCase();
                const email = (u.user_email || '').toLowerCase();
                return name.includes(needle) || email.includes(needle) || code.includes(needle);
              })
            : rows;
          if (filtered.length === 0) {
            return (
              <p className="heda-card px-5 py-10 text-center text-sm text-heda-mute" data-testid="staff-empty-hint">
                {needle ? `No staff matches "${staffSearch}"` : 'No quotations found'}
              </p>
            );
          }
          return (
            <div className="space-y-3">
              {filtered.map((userStat) => (
                <UserStatCard 
                  key={userStat.user_id}
                  userStat={userStat}
                  expandedUser={expandedUser}
                  setExpandedUser={setExpandedUser}
                  initiateDelete={initiateDelete}
                  onEditUser={handleEditUser}
                  navigate={navigate}
                  formatDate={formatDate}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Add User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle>Add Staff User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="user-form-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="user-form-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="user-form-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Staff Code (2 letters)</Label>
                <Input 
                  value={form.staff_code} 
                  onChange={e => setForm(f => ({ ...f, staff_code: e.target.value.toUpperCase().slice(0, 2) }))} 
                  className="rounded-xl mt-1 text-base md:text-sm uppercase bg-heda-cream/50" 
                  placeholder="e.g., SJ"
                  maxLength={2}
                  data-testid="user-form-staff-code" 
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Phone Number</Label>
                <Input 
                  value={form.phone} 
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} 
                  className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" 
                  placeholder="9876543210"
                  data-testid="user-form-phone" 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (Sales - Quotations only)</SelectItem>
                  <SelectItem value="creator">Creator (Creates quotations for salespeople)</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher (Delivery + Stock + Quotations across the shop)</SelectItem>
                  <SelectItem value="delivery">Delivery (Delivery slips only)</SelectItem>
                  <SelectItem value="stock_manager">Stock Manager (Stock + Purchase Orders)</SelectItem>
                  {!isManagerOnly && <SelectItem value="manager">Manager (Products + Quotations)</SelectItem>}
                  {!isManagerOnly && <SelectItem value="admin">Admin (Full access)</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-heda-mute mt-1">
                Staff: Create quotations • Creator: Creates for others • Manager: Products + Quotations • Admin: Full access
              </p>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black w-full" data-testid="user-form-submit">
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User - {editingUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Login Email</Label>
              <Input
                type="email"
                value={editForm.email}
                readOnly
                disabled
                className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream cursor-not-allowed text-heda-sub"
                data-testid="edit-user-current-email"
              />
              <p className="text-[11px] text-heda-mute mt-1">
                Current login. All history and quotations stay linked to this user regardless of email changes.
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">New Email (leave blank to keep)</Label>
              <Input
                type="email"
                value={editForm.new_email}
                onChange={e => setEditForm(f => ({ ...f, new_email: e.target.value }))}
                className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
                autoComplete="off"
                data-testid="edit-user-new-email"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">New Password (leave blank to keep)</Label>
              <Input
                type="text"
                value={editForm.password}
                onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                className="rounded-xl mt-1 text-base md:text-sm font-mono bg-heda-cream/50"
                autoComplete="new-password"
                data-testid="edit-user-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Staff Code</Label>
                <Input 
                  value={editForm.staff_code} 
                  onChange={e => setEditForm(f => ({ ...f, staff_code: e.target.value.toUpperCase().slice(0, 2) }))} 
                  className="rounded-xl mt-1 text-base md:text-sm uppercase bg-heda-cream/50" 
                  placeholder="e.g., SJ"
                  maxLength={2}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Phone</Label>
                <Input 
                  value={editForm.phone} 
                  onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} 
                  className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="creator">Creator</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher (Delivery + Stock + Quotations)</SelectItem>
                  {!isManagerOnly && <SelectItem value="manager">Manager</SelectItem>}
                  {!isManagerOnly && (
                    <SelectItem value="admin" disabled={editingUser?.role !== 'admin'}>Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-xl border border-heda-line bg-heda-cream/40 px-3 py-2.5">
              <div>
                <Label htmlFor="edit-user-active" className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Active</Label>
                <p className="text-[11px] text-heda-mute mt-0.5">
                  {editingUser?.id === currentUser?.id
                    ? 'You cannot deactivate your own account.'
                    : 'Off: cannot sign in, leaves the salesperson lists and gets no WhatsApp reminders. Their records stay.'}
                </p>
              </div>
              <Switch
                id="edit-user-active"
                checked={editForm.is_active}
                disabled={editingUser?.id === currentUser?.id}
                onCheckedChange={(v) => setEditForm(f => ({ ...f, is_active: v }))}
                data-testid="edit-user-active"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60 flex-1" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black flex-1" onClick={() => handleSaveEdit()}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      {/* Passcode Verification Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent className="max-w-xs" data-testid="passcode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-heda-gold" />
              Enter Passcode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-heda-sub">
              {passcodeAction === 'add' ? 'Enter passcode to add a new user' : 'Enter passcode to delete this user'}
            </p>
            <Input
              type="password"
              placeholder="Enter passcode"
              value={passcodeInput}
              onChange={e => setPasscodeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && verifyPasscode()}
              className="rounded-xl text-center text-lg tracking-widest bg-heda-cream/50"
              data-testid="passcode-input"
              autoFocus
            />
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60 flex-1" onClick={() => setShowPasscodeDialog(false)}>
                Cancel
              </Button>
              <Button className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black flex-1" onClick={verifyPasscode}>
                Verify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Passcode Settings Dialog (Admin Only) */}
      <Dialog open={showPasscodeSettings} onOpenChange={setShowPasscodeSettings}>
        <DialogContent className="max-w-sm" data-testid="passcode-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-heda-gold" />
              Change Passcode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">
                  {forgotPasscode ? 'Your login password' : 'Current Passcode'}
                </Label>
                <button
                  type="button"
                  data-testid="forgot-passcode-toggle"
                  onClick={() => {
                    setForgotPasscode(v => !v);
                    setCurrentPasscode('');
                    setAdminLoginPassword('');
                  }}
                  className="text-xs font-medium text-heda-gold hover:underline"
                >
                  {forgotPasscode ? 'Use current passcode' : 'Forgot the passcode?'}
                </button>
              </div>
              {forgotPasscode ? (
                <Input
                  type="password"
                  data-testid="admin-login-password-input"
                  value={adminLoginPassword}
                  onChange={e => setAdminLoginPassword(e.target.value)}
                  placeholder="Enter your account login password"
                  className="rounded-xl mt-1 bg-heda-cream/50"
                />
              ) : (
                <Input
                  type="password"
                  value={currentPasscode}
                  onChange={e => setCurrentPasscode(e.target.value)}
                  placeholder="Enter current passcode"
                  className="rounded-xl mt-1 bg-heda-cream/50"
                />
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">New Passcode</Label>
              <Input
                type="password"
                value={newPasscode}
                onChange={e => setNewPasscode(e.target.value)}
                placeholder="Enter new passcode (min 4 chars)"
                className="rounded-xl mt-1 bg-heda-cream/50"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Confirm New Passcode</Label>
              <Input
                type="password"
                value={confirmPasscode}
                onChange={e => setConfirmPasscode(e.target.value)}
                placeholder="Confirm new passcode"
                className="rounded-xl mt-1 bg-heda-cream/50"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60 flex-1" onClick={() => setShowPasscodeSettings(false)}>
                Cancel
              </Button>
              <Button className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black flex-1" onClick={handleChangePasscode}>
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Share Dialog */}
      <Dialog open={!!credentialsToShare} onOpenChange={(open) => !open && setCredentialsToShare(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2">
              ✓ User Created Successfully
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-heda-sub">
              Share these login details with <strong>{credentialsToShare?.name}</strong> via WhatsApp or any messenger:
            </p>
            <pre className="bg-heda-cream/50 border border-heda-line rounded-xl p-3 text-xs whitespace-pre-wrap font-mono text-heda-ink max-h-60 overflow-auto" data-testid="credentials-message">{buildCredentialMessage(credentialsToShare)}</pre>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleCopyCredentials}
                className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black"
                data-testid="copy-credentials-btn"
              >
                📋 Copy Message
              </Button>
              <Button
                onClick={handleShareWhatsApp}
                variant="outline"
                className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60"
                data-testid="share-whatsapp-btn"
              >
                💬 Send on WhatsApp
              </Button>
            </div>
            <Button
              onClick={() => setCredentialsToShare(null)}
              variant="outline"
              className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60 w-full"
              data-testid="dismiss-credentials-btn"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sub-component for user stat card
function UserStatCard({ userStat, expandedUser, setExpandedUser, initiateDelete, onEditUser, navigate, formatDate }) {
  const { can } = useAuth();
  const isExpanded = expandedUser === userStat.user_id;
  const userInfo = userStat.user_info || {};
  // Local state for the sessions drawer — kept inside the card so opening
  // one user's session list doesn't reset another card's quotation expansion.
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Per-expansion status filter for the quotation grid — 'all' | 'closed'
  // | 'pending' | 'lost'. Reset to 'all' whenever the card collapses so
  // the next expansion starts fresh (per spec).
  const [statusFilter, setStatusFilter] = useState('all');
  useEffect(() => {
    if (!isExpanded) setStatusFilter('all');
  }, [isExpanded]);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await api.getUserSessions(userStat.user_id);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const toggleSessions = async (e) => {
    e.stopPropagation();
    const next = !showSessions;
    setShowSessions(next);
    if (next && !sessions) await loadSessions();
  };

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Force sign-out this device? They will be logged out immediately.')) return;
    try {
      await api.revokeSession(sessionId);
      toast.success('Session revoked');
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm(`Force sign-out ${userStat.user_name} from ALL devices? They'll have to log in again everywhere.`)) return;
    try {
      const res = await api.revokeAllUserSessions(userStat.user_id);
      toast.success(`Revoked ${res.data?.revoked_count || 0} session(s)`);
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  return (
    <div className="heda-card overflow-hidden" data-user-card-id={userStat.user_id}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 hover:bg-heda-cream/40">
        <button
          className="flex-1 min-w-0 flex items-center gap-3 text-left"
          onClick={() => setExpandedUser(isExpanded ? null : userStat.user_id)}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-heda-olive-tint flex items-center justify-center">
            {userInfo.staff_code ? (
              <span className="text-sm font-bold text-heda-olive-dark">{userInfo.staff_code}</span>
            ) : (
              <Users className="w-5 h-5 text-heda-olive-dark" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-heda-ink truncate">
              {userStat.user_name}
              {userInfo.staff_code && (
                <span className="ml-2 rounded-full bg-heda-olive-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-olive-dark">
                  {userInfo.staff_code}
                </span>
              )}
              {userInfo.is_active === false && (
                <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700" data-testid={`inactive-badge-${userStat.user_id}`}>
                  Inactive
                </span>
              )}
            </p>
            <p className="text-[11.5px] text-heda-mute truncate">
              <span className="truncate">{userStat.user_email}</span>
              {userInfo.phone && <span className="ml-2">• {userInfo.phone}</span>}
              {userInfo.phone_invalid && <span className="ml-1 font-semibold text-red-700">(invalid number)</span>}
              {userInfo.role && <span className="ml-2 capitalize">• {userInfo.role}</span>}
            </p>
          </div>
        </button>
        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 pl-13 sm:pl-0 shrink-0">
          <button
            className="text-left sm:text-right"
            onClick={() => setExpandedUser(isExpanded ? null : userStat.user_id)}
          >
            <p className="font-display tabular-nums font-bold text-heda-ink text-sm sm:text-base whitespace-nowrap">
              {userStat.total_quotations} <span className="text-[11px] font-normal text-heda-mute">Quotations</span>
            </p>
            <p className="text-xs tabular-nums text-heda-sub whitespace-nowrap">₹{userStat.total_amount.toLocaleString('en-IN')}</p>
          </button>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-heda-ink hover:text-heda-gold hover:bg-heda-cream h-10 w-10 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onEditUser({ id: userStat.user_id, name: userStat.user_name, email: userStat.user_email, ...userInfo });
            }}
            title="Edit User"
          >
            <Settings className="w-4 h-4" />
          </Button>
          {can('users.sessions') && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-heda-sub hover:text-heda-ink hover:bg-heda-cream h-10 w-10 p-0"
            onClick={toggleSessions}
            title="Recent logins & devices"
            data-testid={`sessions-toggle-${userStat.user_id}`}
          >
            <Monitor className="w-4 h-4" />
          </Button>
          )}
          {userInfo.role !== 'admin' && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full text-red-700 hover:text-red-700 hover:bg-red-50 h-10 w-10 p-0"
              onClick={(e) => {
                e.stopPropagation();
                initiateDelete(userStat);
              }}
              data-testid={`delete-user-${userStat.user_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          </div>
        </div>
      </div>
      
      {/* Login sessions drawer — admin can spot suspicious devices and
          force sign-out a stolen phone without changing the password. */}
      {showSessions && (
        <div className="border-t border-heda-line bg-heda-cream/50 px-4 py-3" data-testid={`sessions-drawer-${userStat.user_id}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="heda-label !mb-0">Recent logins & devices</p>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full border-red-200 bg-white text-xs text-red-700 shadow-none hover:bg-red-50 hover:text-red-700"
              onClick={handleRevokeAll}
              data-testid={`revoke-all-${userStat.user_id}`}
            >
              <ShieldOff className="w-3 h-3 mr-1" />
              Sign out everywhere
            </Button>
          </div>
          {sessionsLoading ? (
            <p className="text-xs text-heda-mute">Loading…</p>
          ) : !sessions || sessions.length === 0 ? (
            <p className="text-xs text-heda-mute">No login history yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className={
                    'flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-xs ' +
                    (s.revoked ? 'border-red-200 bg-red-50 text-heda-mute line-through' : 'border-heda-line bg-white')
                  }
                  data-testid={`session-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {s.device || 'Unknown device'}
                      {s.revoked && <span className="ml-2 not-italic no-underline text-red-700 font-semibold">REVOKED</span>}
                    </p>
                    <p className="text-[11px] text-heda-mute truncate">
                      {s.ip || 'unknown ip'} • {formatIST(s.created_at)}
                    </p>
                  </div>
                  {!s.revoked && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(s.id)}
                      className="text-red-700 hover:underline text-[11.5px] font-semibold shrink-0 min-h-[32px]"
                      data-testid={`revoke-${s.id}`}
                    >
                      Sign out
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expanded Quotations Grid */}
      {isExpanded && (
        <div className="border-t border-heda-line bg-heda-cream/50 px-4 py-3">
          {userStat.quotations.length === 0 ? (
            <p className="text-sm text-heda-mute text-center py-4">No quotations yet</p>
          ) : (() => {
            // Normalise status once so chips + filter agree on the same
            // rule: only 'closed' and 'lost' are recognised outcomes;
            // ANYTHING ELSE (null, empty, legacy values like 'draft' /
            // 'sent' / 'expired', or off-menu strings) folds into
            // 'pending'. This keeps the count and the filter using the
            // exact same value, so the Pending badge can never claim
            // rows the filter then hides.
            const withStatus = userStat.quotations
              .map(q => {
                const raw = (q.status || '').toString().toLowerCase();
                const norm = (raw === 'closed' || raw === 'lost') ? raw : 'pending';
                return { ...q, _norm_status: norm };
              })
              // Newest-first regardless of which filter chip
              // is active. Sort on the normalised list so All / Closed /
              // Pending / Lost tabs all read top-down chronologically.
              .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            const counts = { all: withStatus.length, closed: 0, pending: 0, lost: 0 };
            withStatus.forEach(q => { counts[q._norm_status] += 1; });
            const chips = [
              { key: 'all',     label: 'All',     dot: 'bg-heda-mute' },
              { key: 'closed',  label: 'Closed',  dot: 'bg-emerald-700' },
              { key: 'pending', label: 'Pending', dot: 'bg-amber-500' },
              { key: 'lost',    label: 'Lost',    dot: 'bg-red-700' },
            ];
            const visible = statusFilter === 'all'
              ? withStatus
              : withStatus.filter(q => q._norm_status === statusFilter);
            return (
              <>
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`staff-status-filter-${userStat.user_id}`}>
                  {chips.map(c => {
                    const isActive = statusFilter === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setStatusFilter(c.key)}
                        className={
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] font-medium transition-colors ' +
                          (isActive ? 'bg-heda-ink border-heda-ink text-white' : 'bg-white border-heda-line text-heda-ink hover:border-heda-gold')
                        }
                        data-testid={`staff-status-chip-${c.key}-${userStat.user_id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                        {c.label} <span className={`text-[10.5px] ${isActive ? 'text-white/70' : 'text-heda-mute'}`}>{counts[c.key]}</span>
                      </button>
                    );
                  })}
                </div>
                {visible.length === 0 ? (
                  <p className="text-xs text-heda-mute text-center py-3" data-testid={`staff-status-empty-${userStat.user_id}`}>
                    No {statusFilter} quotations
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {visible.map((q) => {
                // Colour-code the card by outcome so a
                // salesperson's row tells a story at-a-glance.
                //  • pending  → warm yellow tint  (still open)
                //  • closed   → soft green tint   (won)
                //  • lost     → soft red tint     (lost, with reason chip)
                const st = q._norm_status;
                const stateStyles = st === 'closed'
                  ? { chip: 'bg-emerald-50 text-emerald-700', label: '✓ Won' }
                  : st === 'lost'
                    ? { chip: 'bg-red-50 text-red-700', label: '✕ Lost' }
                    : { chip: 'bg-amber-50 text-amber-700', label: '● Pending' };
                return (
                  <button
                    key={q.id}
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    className="rounded-xl border border-heda-line bg-white p-3 text-left transition-colors hover:border-heda-gold group"
                    data-testid={`staff-quotation-card-${q.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-bold text-heda-ink group-hover:underline">
                        {q.quotation_number}
                      </span>
                      <ChevronRight className="w-4 h-4 text-heda-mute group-hover:text-heda-gold" />
                    </div>
                    <p className="text-sm font-medium text-heda-ink truncate mb-1">
                      {q.customer_name || 'No name'}
                    </p>
                    <p className="text-xs text-heda-mute mb-1">
                      {q.customer_mobile || 'No phone'}
                    </p>
                    {/* Status chip — always present so the eye can quickly
                        find pending vs won vs lost even when scanning a
                        long list. */}
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${stateStyles.chip}`}
                      data-testid={`staff-quotation-status-${q.id}`}
                    >
                      {stateStyles.label}
                    </span>
                    {/* Show "Created for" if this quotation was made for a salesperson */}
                    {q.created_for_name && (
                      <p className="text-[11px] text-heda-olive-dark bg-heda-olive-tint px-2 py-0.5 rounded-full mb-2 inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        For: {q.created_for_name} {q.created_for_code && `(${q.created_for_code})`}
                      </p>
                    )}
                    {/* Lost reason chip — only when status=lost AND a reason
                        was recorded. Uses `title` for a hover tooltip in case
                        the reason is longer than the truncated line. */}
                    {st === 'lost' && q.lost_reason && (
                      <p
                        className="text-[11px] text-red-700 bg-red-50 rounded-xl px-2 py-1 mb-2 truncate"
                        title={q.lost_reason}
                        data-testid={`staff-quotation-lost-reason-${q.id}`}
                      >
                        <span className="font-semibold">Why lost:</span> {q.lost_reason}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-heda-line">
                      <span className="tabular-nums text-sm font-bold text-heda-ink">
                        ₹{Math.ceil(q.grand_total).toLocaleString('en-IN')}
                      </span>
                      <span className="text-[11px] text-heda-mute">
                        {formatDate(q.created_at)}
                      </span>
                    </div>
                  </button>
                );
              })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
